import type {
	LayerSpecification,
	Map as MapboxMap,
	FilterSpecification,
	MapLayerMouseEvent,
	MapLayerTouchEvent,
	MapboxGeoJSONFeature,
} from "mapbox-gl";

type PaintOf<T> = T extends { paint?: infer P } ? NonNullable<P> : never;
type LayoutOf<T> = T extends { layout?: infer L } ? NonNullable<L> : never;

type InsertPosition = { beforeId?: string };
type MapboxPaintName = Parameters<MapboxMap["setPaintProperty"]>[1];
type MapboxLayoutName = Parameters<MapboxMap["setLayoutProperty"]>[1];
type PaintName<TSpec> = Extract<keyof PaintOf<TSpec>, string>;
type LayoutName<TSpec> = Extract<keyof LayoutOf<TSpec>, string>;

type LayerEventType = "click" | "mousemove" | "mouseenter" | "mouseleave";

type LayerEvent = MapLayerMouseEvent | MapLayerTouchEvent;

type Bound = { type: LayerEventType; fn: (ev: LayerEvent) => void };

export type LayerEvt = {
	id?: string; // from feature.properties[idProp]
	lng: number;
	lat: number;
	feature: MapboxGeoJSONFeature;
	event: LayerEvent;
	map: MapboxMap;
};

export class MapBoxLayer<TSpec extends LayerSpecification = LayerSpecification> {
	public readonly id: string;
	private spec: TSpec;
	private map: MapboxMap | null = null;
	private insert?: InsertPosition;

	private bound: Bound[] = [];
	private styleLoadUnsub?: () => void;

	constructor(opts: { id: string; spec: TSpec; insert?: InsertPosition }) {
		this.id = opts.id;
		this.spec = opts.spec;
		this.insert = opts.insert;
	}

	attach = (map: MapboxMap, insert?: InsertPosition) => {
		this.map = map;

		// Allow recalculating insertion point per style
		const beforeId = insert?.beforeId ?? this.insert?.beforeId;

		// If the layer exists (e.g., hot reload), keep it; else add it
		if (!map.getLayer(this.id)) {
			if (beforeId) map.addLayer(this.spec, beforeId);
			else map.addLayer(this.spec);
		}

		// (Re)bind handlers
		for (const b of this.bound) map.on(b.type as any, this.id, b.fn as any);

		// Re-add on every style change (not just rebind)
		const onStyleLoad = () => {
			if (!this.map) return;
			const m = this.map;
			// Make sure source exists before layer (layer may reference it)
			const srcId = (this.spec as any).source as string | undefined;
			if (srcId && !m.getSource(srcId)) {
				// caller (Layers class) should have reattached the source,
				// but be defensive: do nothing if missing; Layers will call attach again.
				return;
			}

			if (!m.getLayer(this.id)) {
				const bId = insert?.beforeId ?? this.insert?.beforeId;
				if (bId) m.addLayer(this.spec as any, bId);
				else m.addLayer(this.spec as any);
			}

			for (const b of this.bound) m.on(b.type as any, this.id, b.fn as any);
		};

		// Avoid multiple registrations
		this.styleLoadUnsub?.();
		map.on("style.load", onStyleLoad);
		this.styleLoadUnsub = () => map.off("style.load", onStyleLoad);
	};

	detach = () => {
		if (this.map) {
			for (const b of this.bound) this.map.off(b.type as any, this.id, b.fn as any);
			this.styleLoadUnsub?.();
		}
		this.map = null;
	};

	// ---- Simple handlers you asked for ----
	onClick = (handler: (e: LayerEvt) => void, opts?: { idProp?: string }) => this.bind("click", handler, opts);

	onMouseEnter = (handler: (e: LayerEvt) => void, opts?: { idProp?: string }) =>
		this.bind("mouseenter", handler, opts);

	onMouseMove = (handler: (e: LayerEvt) => void, opts?: { idProp?: string }) => this.bind("mousemove", handler, opts);

	onMouseLeave = (handler: (e: { event: LayerEvent; map: MapboxMap }) => void) => {
		const fn = (ev: LayerEvent) => {
			if (this.map) handler({ event: ev, map: this.map });
		};
		this.bound.push({ type: "mouseleave", fn });
		if (this.map?.getLayer(this.id)) this.map.on("mouseleave", this.id, fn as any);
		return this;
	};

	private bind(type: LayerEventType, handler: (e: LayerEvt) => void, opts?: { idProp?: string }) {
		const idProp = opts?.idProp ?? "id";
		const fn = (ev: LayerEvent) => {
			if (!this.map) return;
			const f = (ev as MapLayerMouseEvent).features?.[0];
			if (!f) return;

			const idVal = f.properties?.[idProp];
			const id = idVal == null ? undefined : String(idVal);

			let { lng, lat } = (ev as MapLayerMouseEvent).lngLat;
			if (f.geometry?.type === "Point") {
				const [x, y] = f.geometry.coordinates as [number, number];
				lng = x;
				lat = y;
			}
			handler({ id, lng, lat, feature: f, event: ev, map: this.map });
		};

		this.bound.push({ type, fn });
		if (this.map?.getLayer(this.id)) this.map.on(type as any, this.id, fn as any);
		return this;
	}

	// ---- Styling API (unchanged) ----
	setFilter = (filter: FilterSpecification | null) => {
		if (!this.map || !this.map.getLayer(this.id)) return;
		this.map.setFilter(this.id, filter);
	};

	setPaint = <K extends PaintName<TSpec>>(name: K, value: PaintOf<TSpec>[K]) => {
		if (!this.map || !this.map.getLayer(this.id)) return;
		this.map.setPaintProperty(this.id, name as unknown as MapboxPaintName, value as any);
	};

	setLayout = <K extends LayoutName<TSpec>>(name: K, value: LayoutOf<TSpec>[K]) => {
		if (!this.map || !this.map.getLayer(this.id)) return;
		this.map.setLayoutProperty(this.id, name as unknown as MapboxLayoutName, value as any);
	};

	setVisibility = (visible: boolean) => {
		this.setLayout("visibility" as LayoutName<TSpec>, (visible ? "visible" : "none") as any);
	};

	setSpec = (spec: TSpec) => {
		this.spec = spec;
	};
}
